import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { supabase } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';
import { Camera, X } from 'lucide-react';

interface ProfileImageUploadProps {
  userId: string;
  currentImage?: string | null;
  onImageUpdate: (url: string) => void;
}

export default function ProfileImageUpload({
  userId,
  currentImage,
  onImageUpdate
}: ProfileImageUploadProps) {
  const [image, setImage] = useState<string | null>(null);
  const [cropper, setCropper] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    },
    maxSize: 5 * 1024 * 1024, // 5MB
    multiple: false
  });

  const handleCrop = async () => {
    if (!cropper || !userId) return;

    try {
      setLoading(true);

      // Get cropped image as blob
      const canvas = cropper.getCroppedCanvas({
        width: 300,
        height: 300
      });
      
      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((blob) => resolve(blob!), 'image/jpeg', 0.9);
      });

      // Create unique filename
      const fileName = `avatars/${userId}-${uuidv4()}.jpg`;
      const file = new File([blob], fileName, { type: 'image/jpeg' });

      // Remove old image if exists
      if (currentImage) {
        try {
          const oldPath = currentImage.split('Public/')[1];
          if (oldPath) {
            await supabase.storage
              .from('Public')
              .remove([oldPath]);
          }
        } catch (err) {
          console.error('Error removing old image:', err);
        }
      }

      // Upload new image
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('Public')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('Public')
        .getPublicUrl(fileName);

      // Update user profile
      const { error: updateError } = await supabase
        .from('stripeusers')
        .update({ profile_image: publicUrl })
        .eq('id', userId);

      if (updateError) throw updateError;

      onImageUpdate(publicUrl);
      setImage(null);
      toast.success('Profile image updated successfully');
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast.error('Failed to update profile image');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Current Image or Upload Area */}
      {!image && (
        <div
          {...getRootProps()}
          className={`relative group cursor-pointer rounded-full overflow-hidden ${
            currentImage
              ? 'w-32 h-32'
              : 'w-32 h-32 border-2 border-dashed border-gray-300'
          }`}
        >
          <input {...getInputProps()} />
          {currentImage ? (
            <>
              <img
                src={currentImage}
                alt="Profile"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <Camera className="w-8 h-8 text-white" />
              </div>
            </>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-gray-400">
              <Camera className="w-8 h-8 mb-2" />
              <p className="text-sm text-center">
                {isDragActive ? 'Drop image here' : 'Click or drag image'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Image Cropper */}
      {image && (
        <div className="space-y-4">
          <div className="relative w-full max-w-md mx-auto">
            <Cropper
              src={image}
              style={{ height: 300, width: '100%' }}
              aspectRatio={1}
              guides={true}
              onInitialized={(instance) => setCropper(instance)}
            />
            <button
              onClick={() => setImage(null)}
              className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors duration-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              onClick={() => setImage(null)}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCrop}
              disabled={loading}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Uploading...' : 'Upload Image'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}